import { addMinutes } from 'date-fns/addMinutes';
import { isBefore } from 'date-fns/isBefore';
import { startOfDay } from 'date-fns/startOfDay';

import { combineRejections } from '../../../core/data-source/base.js';
import {
  type DatabaseProjectionResult,
  dataSourceProjectionColumns,
  getProjectionPropertyIds,
  projectDatabaseRows,
} from '../../../core/data-source/projection.js';
import { dataViewI18nT } from '../../../core/i18n.js';
import { localDateOfDay } from '../../../property-presets/date/calendar-day.js';
import type {
  CalendarColumnIds,
  CalendarSingleView,
} from '../calendar-view-manager.js';
import type { CalendarEvent } from './calendar-view-types.js';
import { guessTimeZone } from './calendar-view-utils.js';

export const CALENDAR_TIMED_EVENT_MINUTES = 15;
/** The length of an event created in an hour slot. */
export const CALENDAR_NEW_EVENT_MINUTES = 60;

export function displayCalendarId(calendarId: string | undefined): string {
  return !calendarId || calendarId === 'Default'
    ? dataViewI18nT('calendar.detail.default')
    : calendarId;
}

export async function createCalendarEventRange(
  view: CalendarSingleView,
  day: Date,
  startMinute: number,
  endMinute: number,
  options: { allDay?: boolean } = {}
): Promise<CalendarEvent | undefined> {
  if (view.readonly$.value) return undefined;
  const columns = await view.ensureCalendarColumnsLive();
  // The new row and its cells are one write of this gesture; a rejected
  // one reaches the caller instead of removing the event unannounced.
  const scope = view.manager.dataSource.openPublicationScope();
  let rowId: string | undefined;
  let failure: unknown;
  try {
    rowId = view.rowAdd('end');
    if (rowId)
      writeCalendarEventCells(
        view,
        rowId,
        columns,
        day,
        startMinute,
        endMinute,
        options
      );
  } catch (error) {
    failure = error;
  }
  const settled = scope.settle();
  if (failure !== undefined) {
    // Both failures reach the caller; neither replaces the other, and the
    // write failure keeps its code.
    await settled.catch(rejection => {
      throw combineRejections([failure, rejection]);
    });
    throw failure;
  }
  await settled;
  if (!rowId) return undefined;
  const event = readCalendarEvent(view, rowId);
  const visible = view.visibleCalendarIds$.value;
  const calendarId = displayCalendarId(event?.calendarId);
  if (visible.length && !visible.includes(calendarId)) {
    view.setVisibleCalendarIds([...visible, calendarId]);
  }
  return event;
}

function writeCalendarEventCells(
  view: CalendarSingleView,
  rowId: string,
  columns: CalendarColumnIds,
  day: Date,
  startMinute: number,
  endMinute: number,
  options: { allDay?: boolean }
): void {
  const normalizedStart = Math.max(0, Math.min(23 * 60 + 45, startMinute));
  const normalizedEnd = Math.max(
    normalizedStart + CALENDAR_TIMED_EVENT_MINUTES,
    endMinute
  );
  const start = addMinutes(startOfDay(day), normalizedStart);
  const end = addMinutes(startOfDay(day), normalizedEnd);

  writeCalendarCellValue(view, rowId, columns.startDateCol, start.getTime());
  writeCalendarCellValue(view, rowId, columns.endDateCol, end.getTime());
  writeCalendarCellValue(
    view,
    rowId,
    columns.allDayCol,
    options.allDay === true
  );
  writeCalendarCellValue(view, rowId, columns.timezoneCol, guessTimeZone());
  writeCalendarCellValue(view, rowId, columns.reminderCol, '15m');
  writeCalendarCellValue(
    view,
    rowId,
    columns.titleColumn,
    dataViewI18nT('calendar.event.newTitle')
  );
}

/** Include this surface's new local rows while the worker projection catches up. */
export function readCalendarProjectedEvents(
  view: CalendarSingleView,
  projection: DatabaseProjectionResult,
  createdRowIds: readonly string[],
  options: { includeDetails?: boolean } = { includeDetails: false }
): CalendarEvent[] {
  const projected = new Set(projection.rowIds);
  const localIds = createdRowIds.filter(id => !projected.has(id));
  const source = view.manager.dataSource;
  const propertyIds = getProjectionPropertyIds(
    projection.query,
    source.getAllPropertyIds()
  );
  const local = localIds.length
    ? projectDatabaseRows({
        columns: dataSourceProjectionColumns(source, propertyIds),
        complete: true,
        freshness: 'live',
        propertyIds,
        query: {
          ...projection.query,
          window: { offset: 0, limit: localIds.length },
        },
        readCellValue: (rowId, propertyId) =>
          view.cellGetOrCreate(rowId, propertyId).jsonValue$.value,
        rowIds: localIds,
        source: 'local-window',
      }).rowIds
    : [];
  return [...projection.rowIds, ...local]
    .map(rowId => readCalendarEvent(view, rowId, options))
    .filter((event): event is CalendarEvent => event !== undefined);
}

export function readCalendarString(
  view: CalendarSingleView,
  rowId: string,
  propertyId: string | undefined
): string {
  if (!propertyId) return '';
  return view.cellGetOrCreate(rowId, propertyId).stringValue$.value ?? '';
}

/**
 * The day of a date cell that holds no time, at local midnight. Every writer
 * stores such a day at UTC midnight of its calendar day.
 */
function dateWithoutTime(value: Date): Date | undefined {
  return value.getUTCHours() === 0 &&
    value.getUTCMinutes() === 0 &&
    value.getUTCSeconds() === 0 &&
    value.getUTCMilliseconds() === 0
    ? localDateOfDay(value.getTime())
    : undefined;
}

export function readCalendarEvent(
  view: CalendarSingleView,
  rowId: string,
  options: { includeDetails?: boolean } = {}
): CalendarEvent | undefined {
  const startCol = view.startDateCol$.value;
  const endCol = view.endDateCol$.value;
  if (!startCol || !endCol) return undefined;

  const startRaw = view.cellGetOrCreate(rowId, startCol).jsonValue$.value;
  const endRaw = view.cellGetOrCreate(rowId, endCol).jsonValue$.value;
  if (typeof startRaw !== 'number') return undefined;
  const written = new Date(startRaw);
  if (!Number.isFinite(written.getTime())) return undefined;

  const allDayCol = view.allDayCol$.value;
  const allDayCell = allDayCol
    ? view.cellGetOrCreate(rowId, allDayCol).jsonValue$.value === true
    : false;
  // A row with a start and no end is a date: all day on that day when the
  // start has no time, else an event of the length a new event gets.
  const openEnded = typeof endRaw !== 'number';
  const day = openEnded ? dateWithoutTime(written) : undefined;
  const allDay = allDayCell || day !== undefined;
  const startAt = day ?? written;
  const endAt = openEnded
    ? allDay
      ? startAt
      : addMinutes(startAt, CALENDAR_NEW_EVENT_MINUTES)
    : new Date(endRaw);
  if (!Number.isFinite(endAt.getTime())) return undefined;

  const titleCol = view.mainProperties$.value.titleColumn;
  const event: CalendarEvent = {
    allDay,
    attendeeCount: 0,
    calendarId: readCalendarString(view, rowId, view.calendarIdCol$.value),
    recurrence: readCalendarString(view, rowId, view.recurrenceCol$.value),
    recurrenceExdate: readCalendarString(
      view,
      rowId,
      view.recurrenceExdateCol$.value
    ),
    recurrenceId: readCalendarString(view, rowId, view.recurrenceIdCol$.value),
    timezone: readCalendarString(view, rowId, view.timezoneCol$.value),
    attendees: '',
    description: '',
    endAt: isBefore(endAt, startAt) ? startAt : endAt,
    locationPhysical: '',
    locationVirtual: '',
    reminder: '',
    rowId,
    startAt,
    status: '',
    title:
      (titleCol
        ? view.cellGetOrCreate(rowId, titleCol).stringValue$.value
        : undefined) || dataViewI18nT('calendar.event.untitled'),
    viewId: view.id,
    visibility: '',
  };
  return options.includeDetails === false
    ? event
    : hydrateCalendarEventDetails(view, event);
}

export function hydrateCalendarEventDetails(
  view: CalendarSingleView,
  event: CalendarEvent
): CalendarEvent {
  if (event.externalReadonly) return event;
  const rowId = event.rowId;
  return {
    ...event,
    attendees: readCalendarString(view, rowId, view.attendeesCol$.value),
    description: readCalendarString(view, rowId, view.descriptionCol$.value),
    locationPhysical: readCalendarString(
      view,
      rowId,
      view.locationPhysicalCol$.value
    ),
    locationVirtual: readCalendarString(
      view,
      rowId,
      view.locationVirtualCol$.value
    ),
    reminder: readCalendarString(view, rowId, view.reminderCol$.value),
    status: readCalendarString(view, rowId, view.statusCol$.value),
    visibility: readCalendarString(view, rowId, view.visibilityCol$.value),
  };
}

/**
 * Runs a calendar write once its role columns are live and reports a failed
 * write on the calendar instead of dropping it.
 */
export async function writeWithLiveCalendarColumns<T>(
  context: {
    view: CalendarSingleView;
    setCalendarError: (message: string | undefined) => void;
  },
  write: (columns: CalendarColumnIds) => T
): Promise<T | undefined> {
  try {
    return write(await context.view.ensureCalendarColumnsLive());
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.chrome.operationFailed')
    );
    return undefined;
  }
}

/** A calendar write named no column for its role. */
export class CalendarColumnMissingError extends Error {
  readonly code = 'CALENDAR_COLUMN_MISSING';

  constructor(readonly rowId: string) {
    super(`Calendar row ${rowId} has no column for the written role.`);
  }
}

export function writeCalendarCellValue(
  view: CalendarSingleView,
  rowId: string,
  propertyId: string | undefined,
  value: unknown
): void {
  if (!propertyId) throw new CalendarColumnMissingError(rowId);
  const property = view.propertyGetOrCreate(propertyId);
  if (property.type$.value === 'select' && typeof value === 'string') {
    property.valueSetFromString(rowId, value);
  } else {
    view.cellGetOrCreate(rowId, propertyId).jsonValueSet(value);
  }
}
